import React from 'react';
import { Phone } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ClickToCallProps {
  phoneNumber?: string;
  displayText?: string;
  className?: string;
  variant?: 'button' | 'link' | 'icon';
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

const ClickToCall: React.FC<ClickToCallProps> = ({
  phoneNumber = '+1-848-339-1572',
  displayText,
  className,
  variant = 'button',
  size = 'md',
  showIcon = true
}) => {
  const formatPhoneNumber = (phone: string): string => {
    // Remove all non-numeric characters for tel: link
    return phone.replace(/[^\d+]/g, '');
  };

  const formatDisplayNumber = (phone: string): string => {
    // Format for display: +1-848-339-1572
    return phone;
  };

  const handleClick = () => {
    // Track click-to-call event for analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'phone_call_click', {
        event_category: 'Contact',
        event_label: 'Click to Call',
        value: 1
      });
    }
  };

  const telLink = `tel:${formatPhoneNumber(phoneNumber)}`;
  const displayNumber = displayText || formatDisplayNumber(phoneNumber);

  const baseClasses = "inline-flex items-center transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2";
  
  const variantClasses = {
    button: "px-4 py-2 rounded-lg font-medium shadow-md hover:shadow-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 focus:ring-blue-500",
    link: "text-blue-600 hover:text-blue-800 underline underline-offset-2 hover:underline-offset-4",
    icon: "p-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl focus:ring-blue-500"
  };

  const sizeClasses = {
    sm: variant === 'button' ? "px-3 py-1.5 text-sm" : "text-sm",
    md: variant === 'button' ? "px-4 py-2 text-base" : "text-base", 
    lg: variant === 'button' ? "px-6 py-3 text-lg" : "text-lg"
  };

  const iconSizes = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6"
  };

  return (
    <a
      href={telLink}
      onClick={handleClick}
      className={cn(
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
      aria-label={`Call ${displayNumber}`}
    >
      {showIcon && variant !== 'icon' && (
        <Phone className={cn(iconSizes[size], "mr-2")} />
      )}
      {variant === 'icon' ? (
        <Phone className={iconSizes[size]} />
      ) : (
        <span>{displayNumber}</span>
      )}
    </a>
  );
};

export default ClickToCall;